<template>
  <v-form ref="form" v-model="isFormValid" @submit.prevent="onSubmit">
    <v-card v-for="section of sections">
      <v-card-title> {{ section.name }}</v-card-title>
      <v-card-text>
        <div v-for="field of section.fields">
          <RubricFieldTextComponent
            v-if="
              field.type == RubricFieldTypeEnum.Text || field.type == RubricFieldTypeEnum.Number
            "
            :field="field"
            :modelValue="getModelValue(field.id)"
            @update:model-value="updateModelValue">
          </RubricFieldTextComponent>
          <RubrciFieldRadioCompontent
            v-if="field.type == RubricFieldTypeEnum.Radio"
            :field="field"
            :modelValue="getModelValue(field.id)"
            @update:model-value="updateModelValue"></RubrciFieldRadioCompontent>
          <RubricFieldCheckboxCompontent
            v-if="field.type == RubricFieldTypeEnum.Checkbox"
            :field="field"
            :modelValue="getModelValue(field.id)"
            @update:model-value="updateModelValue"></RubricFieldCheckboxCompontent>
          <RubricFieldSelectCompontent
            v-if="
              field.type == RubricFieldTypeEnum.MultiSelect ||
              field.type == RubricFieldTypeEnum.Select
            "
            :field="field"
            :modelValue="getModelValue(field.id)"
            @update:model-value="updateModelValue">
          </RubricFieldSelectCompontent>
          <RubricFieldTextAreaCompontent
            v-if="field.type == RubricFieldTypeEnum.TextArea"
            :field="field"
            :modelValue="getModelValue(field.id)"
            @update:model-value="updateModelValue"></RubricFieldTextAreaCompontent>
        </div>
      </v-card-text>
    </v-card>
    <v-btn
      color="primary"
      block
      class="rounded-lg mb-8 mt-8"
      elevation="2"
      :disabled="!isFormValid"
      @click="onSubmit"
      >Save</v-btn
    >
  </v-form>
</template>
<script setup lang="ts">
import {
  RubricDTO,
  RubricSectionDTO,
  IRubricSectionDTO,
  ISubmissionRubricFormDTO,
  IYearDTO,
  SubmissionRubricFormDTO,
  RubricFieldTypeEnum,
} from '@/dtos';
import { useRubricStore, useSubmissionnStore } from '@/stores';
import { computed, ref, watch } from 'vue';
import type { VForm } from 'vuetify/components';
import RubricFieldTextAreaCompontent from './fields/RubricFieldTextAreaCompontent.vue';
import RubricFieldTextComponent from './fields/RubricFieldTextComponent.vue';
import RubricFieldCheckboxCompontent from './fields/RubricFieldCheckboxCompontent.vue';
import RubricFieldSelectCompontent from './fields/RubricFieldSelectCompontent.vue';
import RubrciFieldRadioCompontent from './fields/RubricFieldRadioCompontent.vue';

const props = defineProps<{
  year: IYearDTO;
  submissionRubricForm?: ISubmissionRubricFormDTO;
  submissionId?: string;
}>();
const yearId = computed(() => props.year.id);
const rubricStore = useRubricStore();
const { data } = rubricStore.getRubricByYearQuery(yearId);
const rubric = computed(() => data.value ?? new RubricDTO());
const submissionRubric = ref<ISubmissionRubricFormDTO>(new SubmissionRubricFormDTO());
const sections = ref<IRubricSectionDTO[]>([]);
const isFormValid = ref<boolean>(false);
const form = ref<InstanceType<typeof VForm> | null>();
const submissionStore = useSubmissionnStore();
const updateModelValue = async (fieldId: string, value: string) => {
  let field = submissionRubric.value.fields.filter((field) => field.rubricField.id == fieldId)[0];
  field.value = value;
};

const getModelValue = (fieldId: string) => {
  const field = submissionRubric.value.fields.filter((field) => field.rubricField.id == fieldId)[0];
  return field.value;
};
watch(
  () => rubric.value.sections,
  (value: IRubricSectionDTO[]) => {
    sections.value = value.map((section) => new RubricSectionDTO(section));
  },
  { immediate: true },
);

watch(
  () => props.submissionRubricForm,
  () => {
    submissionRubric.value = new SubmissionRubricFormDTO(props.submissionRubricForm);
  },
  { immediate: true },
);

const onSubmit = async () => {
  if (!submissionRubric.value.isValid() || !props.submissionId) {
    return;
  }
  await rubricStore.updateRubricSubmission.mutateAsync({
    submissionId: props.submissionId,
    submissionRubricForm: submissionRubric.value,
  });
};
</script>
